import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Input } from '@roger/ui-components';
import { Loader2, Plus, X } from 'lucide-react';
import {
  createFaxTag,
  listFaxTags,
  type FaxTag,
} from '../../services/faxClassification';

interface TagPickerProps {
  selected: FaxTag[];
  onChange: (tags: FaxTag[]) => void;
  disabled?: boolean;
  onError: (message: string) => void;
}

/**
 * The agency's tags, searched and added from the same box.
 *
 * Adding is here rather than on a settings screen on purpose: a vocabulary
 * somebody has to leave the fax to extend is a vocabulary that stops being
 * used, and the whole value of tags is that an office can name what it
 * actually cares about.
 *
 * Typing a name that already exists finds it rather than offering to make a
 * second one, because two tags differing only in case would filter
 * differently and nobody means that.
 */
export default function TagPicker({
  selected,
  onChange,
  disabled = false,
  onError,
}: TagPickerProps) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<FaxTag[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  const search = useCallback(
    async (text: string) => {
      setSearching(true);
      try {
        setMatches(await listFaxTags(text || undefined));
      } catch (error) {
        console.error('Failed to search tags', error);
        onError('Could not load the tags.');
      } finally {
        setSearching(false);
      }
    },
    [onError]
  );

  useEffect(() => {
    // Debounced, because this runs per keystroke and the list is a query per
    // call rather than a filter over something already loaded.
    const timer = setTimeout(() => void search(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const selectedIds = useMemo(
    () => new Set(selected.map(tag => tag.id)),
    [selected]
  );
  const offered = matches.filter(tag => !selectedIds.has(tag.id));
  const typed = query.trim();
  // Only when nothing already answers to that name, case insensitively: the
  // server would return the existing tag anyway, and offering to create one
  // that exists reads as a mistake about to happen.
  const canCreate =
    typed !== '' &&
    !matches.some(tag => tag.name.toLowerCase() === typed.toLowerCase()) &&
    !selected.some(tag => tag.name.toLowerCase() === typed.toLowerCase());

  const add = (tag: FaxTag) => {
    onChange([...selected, tag]);
    setQuery('');
  };

  const create = async () => {
    setAdding(true);
    try {
      add(await createFaxTag(typed));
    } catch (error) {
      console.error('Failed to create the tag', error);
      onError('Could not add that tag.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className='space-y-2'>
      {selected.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {selected.map(tag => (
            <Badge key={tag.id} variant='secondary' className='gap-1'>
              {tag.name}
              <button
                type='button'
                aria-label={`Remove tag ${tag.name}`}
                disabled={disabled}
                onClick={() =>
                  onChange(selected.filter(other => other.id !== tag.id))
                }
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* The list floats over what is below rather than pushing it down, the
          way the type picker beside it does, so choosing a tag does not move
          the rest of the dialog under the pointer. */}
      <div className='relative'>
        <Input
          aria-label='Search tags'
          placeholder='Search or add a tag'
          value={query}
          disabled={disabled}
          onChange={event => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          // Closed on blur, except when the blur came from reaching for the
          // list itself: the panel takes the mousedown back below.
          onBlur={() => setOpen(false)}
        />

        {open && (
          <div
            className='absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-sm'
            // Keeps the input focused, so clicking an option is a click
            // rather than a blur that closes the list out from under it.
            onMouseDown={event => event.preventDefault()}
          >
            {/* The previous matches stay on screen while the next search runs.
                Blanking the list on every keystroke made it flicker, and a
                list that disappears as you type is one you cannot aim at. */}
            {offered.map(tag => (
              <button
                key={tag.id}
                type='button'
                className='block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                disabled={disabled}
                onClick={() => add(tag)}
              >
                {tag.name}
              </button>
            ))}

            {canCreate && (
              <button
                type='button'
                className='flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                disabled={disabled || adding}
                onClick={() => void create()}
              >
                {adding ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Plus className='mr-2 h-4 w-4' />
                )}
                Add {typed}
              </button>
            )}

            {offered.length === 0 && !canCreate && (
              <p className='px-2 py-1.5 text-sm text-muted-foreground'>
                {searching
                  ? 'Searching\u2026'
                  : typed
                    ? 'Already chosen.'
                    : 'No tags yet.'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
